/* ATHR reviews (sections/athr-reviews.liquid).
   Form: photos are resized in the browser (max 1800px, JPEG) so uploads are quick on mobile data,
   then everything is uploaded straight to Shopify storage through staged targets handed out by the
   ATHR Reviews app proxy, and the review is submitted with the uploaded file URLs.
   Display: "with photos" filter and a lightbox for review photos/videos. */
(() => {
  const root = document.getElementById('reviews');
  if (!root || root.dataset.athrReady) return;
  root.dataset.athrReady = '1';

  const MAX_IMAGES = 5;
  const MAX_VIDEO_MB = 150;
  const MAX_VIDEO_SECONDS = 90;
  const IMAGE_EDGE = 1800;
  const t = JSON.parse(root.querySelector('[data-athr-review-i18n]').textContent);

  // ---------------------------------------------------------------- modal
  const modal = root.querySelector('[data-athr-review-modal]');
  const form = root.querySelector('[data-athr-review-form]');
  let openedAt = 0;
  root.querySelectorAll('[data-athr-review-open]').forEach((b) => b.addEventListener('click', () => {
    openedAt = openedAt || Date.now();
    modal.showModal();
  }));
  root.querySelectorAll('[data-athr-review-close]').forEach((b) => b.addEventListener('click', () => modal.close()));
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });
  if (location.hash === '#write-review') { openedAt = Date.now(); modal.showModal(); }

  // ---------------------------------------------------------------- media picker
  const input = form.querySelector('[data-athr-files]');
  const grid = form.querySelector('[data-athr-previews]');
  const addTile = grid.querySelector('.athr-upload__add');
  let picked = []; // { file, kind, url }

  input.addEventListener('change', async () => {
    hideError();
    for (const file of [...input.files]) {
      const kind = file.type.startsWith('video/') ? 'video' : file.type.startsWith('image/') ? 'image' : null;
      if (!kind) continue;
      if (kind === 'video') {
        if (picked.some((p) => p.kind === 'video')) { showError(t.err_video); continue; }
        if (file.size > MAX_VIDEO_MB * 1024 * 1024 || (await videoSeconds(file)) > MAX_VIDEO_SECONDS) { showError(t.err_video); continue; }
      } else if (picked.filter((p) => p.kind === 'image').length >= MAX_IMAGES) {
        showError(t.err_files); continue;
      }
      picked.push({ file, kind, url: URL.createObjectURL(file) });
    }
    input.value = '';
    renderPreviews();
  });

  function renderPreviews() {
    grid.querySelectorAll('.athr-upload__item').forEach((n) => n.remove());
    picked.forEach((p, i) => {
      const item = document.createElement('div');
      item.className = 'athr-upload__item';
      item.innerHTML = p.kind === 'video'
        ? `<video src="${p.url}" muted playsinline preload="metadata"></video><span class="athr-media__play" aria-hidden="true">▶</span>`
        : `<img src="${p.url}" alt="">`;
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'athr-upload__remove';
      rm.setAttribute('aria-label', t.remove);
      rm.textContent = '×';
      rm.addEventListener('click', () => { URL.revokeObjectURL(p.url); picked.splice(i, 1); renderPreviews(); });
      item.appendChild(rm);
      grid.insertBefore(item, addTile);
    });
    addTile.hidden = picked.filter((p) => p.kind === 'image').length >= MAX_IMAGES && picked.some((p) => p.kind === 'video');
  }

  function videoSeconds(file) {
    return new Promise((resolve) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration || 0); };
      v.onerror = () => resolve(0);
      v.src = URL.createObjectURL(file);
    });
  }

  // Resize to IMAGE_EDGE on the long side and re-encode as JPEG; falls back to the original
  // file if the browser can't decode it (e.g. HEIC on desktop Chrome).
  async function shrinkImage(file) {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const scale = Math.min(1, IMAGE_EDGE / Math.max(bmp.width, bmp.height));
      if (scale === 1 && file.type === 'image/jpeg' && file.size < 1.5e6) return file;
      const c = document.createElement('canvas');
      c.width = Math.round(bmp.width * scale);
      c.height = Math.round(bmp.height * scale);
      c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
      const blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.86));
      return blob ? new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
    } catch {
      return file;
    }
  }

  // ---------------------------------------------------------------- submit
  const submitBtn = form.querySelector('[data-athr-submit]');
  const progress = form.querySelector('[data-athr-progress]');
  const bar = progress.querySelector('span');
  const endpoint = form.dataset.endpoint;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    hideError();
    const fd = new FormData(form);
    const rating = Number(fd.get('rating'));
    const body = String(fd.get('body') || '').trim();
    const name = String(fd.get('name') || '').trim();
    const email = String(fd.get('email') || '').trim();
    if (!rating) return showError(t.err_rating);
    if (body.length < 10) return showError(t.err_body);
    if (!name) return showError(t.err_name);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return showError(t.err_email);

    submitBtn.disabled = true;
    const label = submitBtn.textContent;
    try {
      let media = [];
      if (picked.length) {
        submitBtn.textContent = t.uploading;
        progress.hidden = false;
        const files = await Promise.all(picked.map((p) => (p.kind === 'image' ? shrinkImage(p.file) : p.file)));
        const res = await post('/uploads', { files: files.map((f) => ({ name: f.name, type: f.type || 'image/jpeg', size: f.size })) });
        const total = files.reduce((s, f) => s + f.size, 0);
        const done = new Array(files.length).fill(0);
        await Promise.all(files.map((f, i) => upload(res.targets[i], f, (n) => {
          done[i] = n;
          bar.style.width = Math.min(100, (done.reduce((a, b) => a + b, 0) / total) * 100) + '%';
        })));
        media = files.map((f, i) => ({ resourceUrl: res.targets[i].resourceUrl, kind: picked[i].kind }));
      }
      submitBtn.textContent = t.sending;
      await post('/submit', {
        product_id: form.dataset.productId,
        lang: form.dataset.lang,
        rating, body, name, email,
        title: String(fd.get('title') || '').trim(),
        city: String(fd.get('city') || '').trim(),
        website: String(fd.get('website') || ''),
        elapsed: Date.now() - openedAt,
        media,
      });
      picked.forEach((p) => URL.revokeObjectURL(p.url));
      picked = [];
      renderPreviews();
      form.reset();
      form.querySelector('[data-step="form"]').hidden = true;
      form.querySelector('[data-step="done"]').hidden = false;
    } catch (err) {
      showError(err && err.status === 429 ? t.err_rate : t.err_generic);
      console.error('ATHR review submit', err);
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = label;
      progress.hidden = true;
      bar.style.width = '0';
    }
  });

  async function post(path, data) {
    const res = await fetch(endpoint + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data),
      credentials: 'same-origin',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) { const e = new Error(json.error || 'request'); e.status = res.status; throw e; }
    return json;
  }

  function upload(target, file, onProgress) {
    return new Promise((resolve, reject) => {
      const data = new FormData();
      target.parameters.forEach((p) => data.append(p.name, p.value));
      data.append('file', file);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', target.url);
      xhr.upload.onprogress = (e) => onProgress(e.loaded);
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? (onProgress(file.size), resolve()) : reject(new Error('upload ' + xhr.status)));
      xhr.onerror = () => reject(new Error('upload network'));
      xhr.send(data);
    });
  }

  const errEl = form.querySelector('[data-athr-error]');
  function showError(msg) { errEl.textContent = msg; errEl.hidden = false; }
  function hideError() { errEl.hidden = true; }

  // ---------------------------------------------------------------- filter
  root.querySelectorAll('[data-athr-filter]').forEach((b) => b.addEventListener('click', () => {
    root.querySelectorAll('[data-athr-filter]').forEach((x) => x.classList.toggle('is-active', x === b));
    const onlyMedia = b.dataset.athrFilter === 'media';
    root.querySelectorAll('.athr-review').forEach((li) => { li.hidden = onlyMedia && li.dataset.hasMedia !== 'true'; });
  }));

  // ---------------------------------------------------------------- lightbox
  const lb = root.querySelector('[data-athr-lightbox]');
  const stage = lb.querySelector('[data-athr-lightbox-stage]');
  const caption = lb.querySelector('[data-athr-lightbox-caption]');
  let items = [];
  let index = 0;

  function show(i) {
    index = (i + items.length) % items.length;
    const el = items[index];
    stage.innerHTML = el.dataset.kind === 'video'
      ? `<video src="${el.dataset.src}" controls autoplay playsinline></video>`
      : `<img src="${el.dataset.src}" alt="">`;
    caption.textContent = el.dataset.caption || '';
  }
  root.addEventListener('click', (e) => {
    const el = e.target.closest('[data-athr-media]');
    if (!el) return;
    const scope = el.closest('.athr-reviews__strip, .athr-review__media');
    items = [...scope.querySelectorAll('[data-athr-media]')];
    show(items.indexOf(el));
    lb.showModal();
  });
  lb.querySelector('[data-athr-lightbox-close]').addEventListener('click', () => lb.close());
  lb.querySelector('[data-athr-lightbox-prev]').addEventListener('click', () => show(index - 1));
  lb.querySelector('[data-athr-lightbox-next]').addEventListener('click', () => show(index + 1));
  lb.addEventListener('click', (e) => { if (e.target === lb) lb.close(); });
  lb.addEventListener('close', () => { stage.innerHTML = ''; });
  lb.addEventListener('keydown', (e) => {
    const rtl = document.documentElement.dir === 'rtl';
    if (e.key === 'ArrowRight') show(index + (rtl ? -1 : 1));
    if (e.key === 'ArrowLeft') show(index + (rtl ? 1 : -1));
  });
})();
